/**
 * Custom Sidebar Component
 *
 * A clean, custom sidebar with full control over styling and behavior.
 * Features:
 * - Collapsed/expanded state persisted to localStorage
 * - Hover-to-expand with configurable delay
 * - Keyboard shortcuts ([ and Ctrl+B)
 * - Clean CSS without ProLayout overrides
 */

import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { Menu, Badge, Tooltip } from 'antd';
import { Link } from '@/lib/router';
import { useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useAuth, useModel } from '@/context/AuthContext';
import { useTabsContextSafe } from '@/context/TabsContext';
import { menuIcons, getMenuIcon } from './menuIcons';
import { isPermitted, ModuleName, PermissionVerbAction } from '@/components/Guard';
import styles from './AppLayout.module.less';

// Storage key for sidebar collapse preference
const SIDEBAR_COLLAPSED_KEY = 'utx-sidebar-collapsed';

type MenuItem = Required<MenuProps>['items'][number];

interface SidebarProps {
  collapsed: boolean;
  hovering: boolean;
  onCollapse: (collapsed: boolean) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Build menu items from the backend menu data
 */
function buildMenuItems(
  menuData: any[] | undefined,
  initialState: any,
  location: string,
  sidebarBehavior?: 'new-tab' | 'replace' | 'reuse',
): MenuItem[] {
  if (!menuData) return [];

  return menuData
    .map((item): MenuItem | null => {
      // Skip hidden items
      // Skip detail routes (with :id params) - these should not appear in menu
      if (item.path && item.path.includes(':')) return null;

      // Skip items without a name
      if (!item.name) return null;
      if (item.hideInMenu) return null;

      // Check permissions if ACL is defined
      if (item.acl) {
        const hasPermission = isPermitted(
          item.acl.module,
          item.acl.action,
          initialState?.currentUser?.acl
        );
        if (!hasPermission) return null;
      }

      // Get the icon
      const icon = item.icon ? getMenuIcon(item.icon) : getMenuIcon('appstore');

      // Build badge count based on path
      let badgeCount: number | undefined;
      if (item.path === '/pending-instructions/cra-form') {
        badgeCount = initialState?.craFormInstructions;
      } else if (item.path === '/pending-instructions/payments') {
        badgeCount = initialState?.payments;
      } else if (item.path === '/pending-instructions/instructions') {
        badgeCount = initialState?.instructions;
      } else if (item.path === '/digital-custody/deposits/pending') {
        badgeCount = initialState?.digitalDepositPending;
      } else if (item.path === '/solution-application') {
        badgeCount = initialState?.solutionApplications;
      } else if (item.path === '/cms/card-application') {
        badgeCount = initialState?.cardApplicationCount;
      } else if (item.path === '/cms/card-pending-embossed') {
        badgeCount = initialState?.cardPendingEmbossedCount;
      }

      // Calculate pending instructions total for parent menu
      if (item.name === 'Pending Instructions') {
        const total =
          (initialState?.craFormInstructions || 0) +
          (initialState?.payments || 0) +
          (initialState?.instructions || 0);
        if (total > 0) {
          badgeCount = total;
        }
      }

      // Handle children recursively (UmiJS uses 'routes', ProLayout uses 'children')
      const childItems = item.children || item.routes;
      const children = childItems
        ? buildMenuItems(childItems, initialState, location, sidebarBehavior)
        : undefined;

      // Build the label with optional badge
      const labelContent = badgeCount ? (
        <span className={styles.menuItemLabel}>
          <span>{item.name}</span>
          <Badge count={badgeCount} overflowCount={999999} size="small" className={styles.menuBadge} />
        </span>
      ) : (
        item.name
      );

      // Check if this item has actual children after processing
      const hasChildren = children && children.length > 0;

      // Wrap in Link if it has a path and no children (leaf node)
      const label =
        item.path && !hasChildren ? (
          <Link to={item.path} behavior={sidebarBehavior}>{labelContent}</Link>
        ) : (
          labelContent
        );

      return {
        key: item.path || item.name,
        icon,
        label,
        children: hasChildren ? children : undefined,
      };
    })
    .filter((item): item is MenuItem => item !== null);
}

/**
 * Find the currently active menu key based on location
 */
function getActiveKey(pathname: string, menuData: any[] | undefined): string[] {
  if (!menuData) return [];

  // Find exact match first
  for (const item of menuData) {
    if (item.path === pathname) {
      return [item.path];
    }
    if (item.children) {
      for (const child of item.children) {
        if (child.path === pathname) {
          return [child.path];
        }
      }
    }
  }

  // Find prefix match
  for (const item of menuData) {
    if (item.path && pathname.startsWith(item.path)) {
      return [item.path];
    }
    if (item.children) {
      for (const child of item.children) {
        if (child.path && pathname.startsWith(child.path)) {
          return [child.path];
        }
      }
    }
  }

  return [];
}

/**
 * Get open keys for submenus
 */
function getOpenKeys(pathname: string, menuData: any[] | undefined): string[] {
  if (!menuData) return [];

  for (const item of menuData) {
    if (item.children) {
      for (const child of item.children) {
        if (child.path && (pathname === child.path || pathname.startsWith(child.path))) {
          return [item.path || item.name];
        }
      }
    }
  }

  return [];
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  hovering,
  onCollapse,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { initialState } = useAuth();
  const location = useLocation();
  const tabsContext = useTabsContextSafe();
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Extract only the path from activeTab to avoid re-renders when lastAccessed changes
  // The useMemo below depends on activeTab object, but returns a primitive string,
  // so downstream memos only recompute when the actual path changes
  const activeTabPath = useMemo(() => {
    const activeTab = tabsContext?.activeTab;
    if (!activeTab) return null;
    const entry = activeTab.history[activeTab.historyIndex];
    return entry?.path || activeTab.path;
  }, [tabsContext?.activeTab]);

  // Use activeTab path if available, otherwise fall back to react-router location
  const currentPath = activeTabPath || location.pathname;

  // Effective collapsed state (collapsed unless hovering)
  const effectiveCollapsed = collapsed && !hovering;

  // Build menu items with config-driven sidebar click behavior
  const sidebarBehavior = tabsContext?.config.defaultBehavior;
  const menuItems = useMemo(
    () => buildMenuItems(initialState?.menuData, initialState, currentPath, sidebarBehavior),
    [initialState?.menuData, initialState, currentPath, sidebarBehavior]
  );

  // Get active and open keys
  const selectedKeys = useMemo(
    () => getActiveKey(currentPath, initialState?.menuData),
    [currentPath, initialState?.menuData]
  );

  // Controlled openKeys state for submenus
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Sync openKeys when location changes
  useEffect(() => {
    if (!effectiveCollapsed) {
      const keys = getOpenKeys(currentPath, initialState?.menuData);
      // Only update if we have new keys to open (don't close manually opened submenus)
      if (keys.length > 0) {
        setOpenKeys((prev) => {
          // Merge: keep existing open keys and add new ones
          const merged = [...new Set([...prev, ...keys])];
          return merged;
        });
      }
    }
  }, [currentPath, initialState?.menuData, effectiveCollapsed]);

  // Handle manual open/close of submenus
  const handleOpenChange = useCallback((keys: string[]) => {
    setOpenKeys(keys);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Toggle with [ key (like VS Code) or Ctrl+B
      if (e.key === '[' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement;
        // Don't trigger if typing in an input
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }
        e.preventDefault();
        onCollapse(!collapsed);
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        onCollapse(!collapsed);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [collapsed, onCollapse]);

  // Handle collapse button click
  const handleCollapseClick = useCallback(() => {
    onCollapse(!collapsed);
  }, [collapsed, onCollapse]);

  return (
    <aside
      ref={sidebarRef}
      className={`${styles.sidebar} ${effectiveCollapsed ? styles.sidebarCollapsed : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Logo area */}
      <div className={styles.sidebarLogo}>
        {!effectiveCollapsed && (
          <span className={styles.sidebarLogoText}>
            {initialState?.settings?.layoutTitle || 'Admin'}
          </span>
        )}
        <Tooltip title={collapsed ? 'Expand ([ or ⌘B)' : 'Collapse ([ or ⌘B)'} placement="right">
          <button
            type="button"
            className={styles.collapseButton}
            onClick={handleCollapseClick}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </Tooltip>
      </div>

      {/* Menu */}
      <div className={styles.sidebarMenu}>
        <Menu
          mode="inline"
          theme="dark"
          inlineCollapsed={effectiveCollapsed}
          items={menuItems}
          selectedKeys={selectedKeys}
          openKeys={effectiveCollapsed ? [] : openKeys}
          onOpenChange={handleOpenChange}
          className={styles.menu}
        />
      </div>
    </aside>
  );
};

export default Sidebar;

